import { AspectRatio } from 'components/commons/aspect-ratio';
import DialogBackdrop from 'components/commons/dialog/dialog-backdrop';
import IconBox from 'components/commons/icon-box';
import Stack from 'components/commons/stack';
import Image from 'next/image';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { ChevronLeft, ChevronRight, X } from 'react-feather';
import color from 'styles/color';

import {
  GallerySlideShow,
  GalleryTitleText,
  ImageContainer,
  ImageList,
  ImageThumbnailContainer,
  PictureText,
  StyledGalleryContent,
  StyledGalleryDialog,
  StyledGalleryHeader,
} from './style';

interface IGalleryProps {
  onClose: () => void;
  imageList: { alt: string; src: string }[];
}

export default function Gallery(props: IGalleryProps) {
  const { t } = useTranslation();
  const { onClose, imageList } = props;
  const [activeImage, setActiveImage] = React.useState(0);

  const onPrevGallery = () => {
    if (activeImage === 0) {
      setActiveImage(imageList.length - 1);
    } else {
      setActiveImage((prev) => prev - 1);
    }
  };

  const onNextGallery = () => {
    if (activeImage === imageList.length - 1) {
      setActiveImage(0);
    } else {
      setActiveImage((prev) => prev + 1);
    }
  };

  return (
    <DialogBackdrop cancelable onClose={onClose}>
      <StyledGalleryDialog>
        <StyledGalleryHeader>
          <IconBox css={{ width: 24 }} />
          <GalleryTitleText weight="bold">
            {t('common:gallery')}
          </GalleryTitleText>
          <IconBox onClick={onClose}>
            <X color={color.white} />
          </IconBox>
        </StyledGalleryHeader>
        <StyledGalleryContent>
          <GallerySlideShow>
            <Stack direction="horizontal" alignItems="center">
              <Stack.Item
                css={{
                  cursor: 'pointer',
                  height: '100%',
                  '&:hover': {
                    backgroundColor: '$neutral1',
                  },
                }}
                onClick={onPrevGallery}
                justify="center"
              >
                <ChevronLeft />
              </Stack.Item>
              <Stack.Item grow>
                {imageList[activeImage] && (
                  <AspectRatio ratio={4 / 3}>
                    <ImageContainer>
                      <Image
                        style={{ objectFit: 'contain' }}
                        fill
                        src={imageList[activeImage].src}
                        alt={imageList[activeImage].alt}
                      />
                    </ImageContainer>
                  </AspectRatio>
                )}
              </Stack.Item>
              <Stack.Item
                css={{
                  cursor: 'pointer',
                  height: '100%',
                  '&:hover': {
                    backgroundColor: '$neutral1',
                  },
                }}
                onClick={onNextGallery}
                justify="center"
              >
                <ChevronRight />
              </Stack.Item>
            </Stack>
            <Stack direction="horizontal" justify="between" rootCss={{ mt: 8 }}>
              <PictureText>{t('common:picture')}</PictureText>
              <PictureText>{`${activeImage + 1}/${
                imageList.length
              }`}</PictureText>
            </Stack>
          </GallerySlideShow>
          <ImageList>
            {imageList.map((item, idx) => (
              <ImageThumbnailContainer key={item.alt}>
                <Image
                  fill
                  style={{ objectFit: 'cover' }}
                  src={item.src}
                  alt={item.alt}
                  onClick={() => setActiveImage(idx)}
                />
              </ImageThumbnailContainer>
            ))}
          </ImageList>
        </StyledGalleryContent>
      </StyledGalleryDialog>
    </DialogBackdrop>
  );
}
